import { create } from 'zustand';

// 画像のピクセル情報の型定義
interface ImageDimensions {
  width: number;
  height: number;
}

// 商品バリエーションの型定義
interface ProductVariant {
  id: number;
  title: string;
  price: string;
  inventory_quantity: number;
}

// 選択された商品の型定義
interface SelectedProduct {
  id: number;
  title: string;
  handle: string;
  variants: ProductVariant[];
  images: Array<{
    id: number;
    src: string;
    alt: string;
  }>;
  tags: string | string[];
  product_type: string;
}

// カスタムオプションの型定義
interface CustomOptionRule {
  field: string;
  operator: 'is' | 'is_not_empty' | 'is_empty';
  value: string;
}

interface CustomOptionRelation {
  field?: string;
  operator?: 'is' | 'is_not_empty' | 'is_empty';
  value?: string;
  rules?: CustomOptionRule[];
}

interface CustomOption {
  id: string;
  label: string;
  type: 'select' | 'radio' | 'checkbox' | 'number' | 'textarea' | 'content';
  required: boolean;
  values?: Array<{
    label: string;
    value: string;
    selected?: boolean;
  }>;
  placeholder?: string;
  description?: string;
  value?: string; // content用
  min?: number;
  max?: number;
  relations?: CustomOptionRelation[];
}

// カテゴリーの型定義
export type Category = 'print' | 'frame' | 'mat-processing' | 'panel' | 'canvas';

// ストアの状態の型定義
interface OrderState {
  selectedCategory: Category;
  currentPage: number;
  frameType: 'print-only' | 'framed' | '';
  uploadedImage: string | null;
  originalImage: string | null; // Q2でアップロードした元の画像（リセット用）
  croppedImage: string | null; // トリミング後の画像
  imageDimensions: ImageDimensions | null;
  selectedSize: string; // バリエーションIDを文字列として保存
  selectedFrame: string;
  selectedMat: string;
  selectedFrameType: string; // 額装A、B、C
  selectedProduct: SelectedProduct | null; // 選択された商品
  selectedFrameProduct: SelectedProduct | null; // 選択された額装商品
  customOptions: Record<string, string | string[]>; // カスタムオプションの選択値
  productOptions: Record<string, any>; // JSON定義オプションの選択値
  deliverySize: '定形サイズ' | '定形外サイズ' | ''; // 納品サイズの選択
  customDeliverySize: { shortEdge: number; longEdge: number } | null; // 定形外サイズの寸法
  customPaperWidth: number | null; // カスタム用紙幅 (mm)
  customPaperHeight: number | null; // カスタム用紙高さ (mm)
}

// アクションの型定義
interface OrderActions {
  setSelectedCategory: (category: Category) => void;
  setCurrentPage: (page: number) => void;
  setFrameType: (type: 'print-only' | 'framed' | '') => void;
  setImage: (image: string | null) => void;
  setCroppedImage: (image: string | null) => void; // トリミング後の画像を設定
  setImageDimensions: (dimensions: ImageDimensions | null) => void;
  setSize: (size: string) => void;
  setFrame: (frame: string) => void;
  setMat: (mat: string) => void;
  setFrameTypeSelection: (frameType: string) => void;
  setSelectedProduct: (product: SelectedProduct | null) => void;
  setSelectedFrameProduct: (product: SelectedProduct | null) => void;
  setCustomOption: (optionId: string, value: string | string[]) => void;
  setProductOption: (fieldId: string, value: any) => void;
  setProductOptions: (values: Record<string, any>) => void;
  clearProductOptions: () => void;
  setDeliverySize: (size: '定形サイズ' | '定形外サイズ' | '') => void;
  setCustomDeliverySize: (size: { shortEdge: number; longEdge: number } | null) => void;
  setCustomPaperDimensions: (width: number | null, height: number | null) => void;
  nextPage: () => void;
  prevPage: () => void;
  resetOrder: () => void;
}

// ストアの型定義（状態 + アクション）
type OrderStore = OrderState & OrderActions;

// 初期状態
const initialState: OrderState = {
  selectedCategory: 'print',
  currentPage: 1,
  frameType: '',
  uploadedImage: null,
  originalImage: null, // Q2でアップロードした元の画像（リセット用）
  croppedImage: null, // トリミング後の画像
  imageDimensions: null,
  selectedSize: '',
  selectedFrame: '',
  selectedMat: '',
  selectedFrameType: '',
  selectedProduct: null,
  selectedFrameProduct: null,
  customOptions: {},
  productOptions: {},
  deliverySize: '',
  customDeliverySize: null,
  customPaperWidth: null,
  customPaperHeight: null,
};

// Zustandストアの作成
export const useOrderStore = create<OrderStore>((set) => ({
  // 初期状態
  ...initialState,

  // アクション
  setSelectedCategory: (category) => set({ selectedCategory: category }),
  setCurrentPage: (page) => set({ currentPage: page }),

  setFrameType: (type) => set({ frameType: type }),

  setImage: (image) => {
    console.log('Zustand Store: setImage呼び出し', image ? image.substring(0, 50) + '...' : 'null');
    console.log('Zustand Store: 現在のuploadedImage:', image ? image.substring(0, 50) + '...' : 'null');
    console.log('Zustand Store: DataURL長さ:', image ? image.length : 0);

    set((state) => {
      // originalImageがnullの場合（最初のアップロード時）のみ、originalImageに保存
      const newState: Partial<OrderState> = { uploadedImage: image };
      if (image && !state.originalImage) {
        newState.originalImage = image;
        console.log('Zustand Store: originalImageを保存しました（最初のアップロード）');
      }
      return newState;
    });
    console.log('Zustand Store: setImage完了');

    // 少し待ってから状態を確認
    setTimeout(() => {
      console.log('Zustand Store: 1秒後の状態確認');
    }, 1000);
  },

  setCroppedImage: (image) => {
    console.log('Zustand Store: setCroppedImage呼び出し', image ? image.substring(0, 50) + '...' : 'null');
    set({ croppedImage: image });
    console.log('Zustand Store: setCroppedImage完了');
  },

  setImageDimensions: (dimensions) => set({ imageDimensions: dimensions }),

  setSize: (size) => set({ selectedSize: size }),

  setFrame: (frame) => set({ selectedFrame: frame }),

  setMat: (mat) => set({ selectedMat: mat }),

  setFrameTypeSelection: (frameType) => set({ selectedFrameType: frameType }),

  setSelectedProduct: (product) => set({ selectedProduct: product }),

  setSelectedFrameProduct: (product) => {
    console.log('Zustand Store: setSelectedFrameProduct呼び出し', product);
    set({ selectedFrameProduct: product });

    // Q5で選択された額装商品をQ1の商品に追加する
    set((state) => {
      if (state.selectedProduct && product) {
        console.log('Zustand Store: Q1商品に額装商品を追加');
        // ここでQ1の商品にQ5の商品を追加する処理を実装
        // 現在は単純にselectedFrameProductとして保存
        return { selectedFrameProduct: product };
      }
      return { selectedFrameProduct: product };
    });
  },

  setCustomOption: (optionId, value) => set((state) => ({
    customOptions: { ...state.customOptions, [optionId]: value }
  })),

  setProductOption: (fieldId, value) => set((state) => ({
    productOptions: { ...state.productOptions, [fieldId]: value }
  })),

  setProductOptions: (values) => set((state) => ({
    productOptions: { ...state.productOptions, ...values }
  })),

  clearProductOptions: () => set({ productOptions: {} }),

  setDeliverySize: (size) => set({ deliverySize: size }),

  setCustomDeliverySize: (size) => set({ customDeliverySize: size }),

  setCustomPaperDimensions: (width, height) => set({ customPaperWidth: width, customPaperHeight: height }),

  nextPage: () => set((state) => ({ currentPage: state.currentPage + 1 })),

  prevPage: () => set((state) => ({ currentPage: Math.max(1, state.currentPage - 1) })),

  // すべての状態をリセット
  resetOrder: () => set(initialState),
}));

// セレクター関数（オプション）
export const selectImage = (state: OrderStore) => state.uploadedImage;
export const selectSize = (state: OrderStore) => state.selectedSize;
export const selectFrame = (state: OrderStore) => state.selectedFrame;
export const selectMat = (state: OrderStore) => state.selectedMat;
export const selectSelectedProduct = (state: OrderStore) => state.selectedProduct;

// 型定義のエクスポート
export type { SelectedProduct, ProductVariant, CustomOption, CustomOptionRelation };

// すべての選択状態を取得するセレクター
export const selectAllSelections = (state: OrderStore) => ({
  image: state.uploadedImage,
  size: state.selectedSize,
  frame: state.selectedFrame,
  mat: state.selectedMat,
});

// 注文が完了可能かどうかを判定するセレクター
export const selectIsOrderComplete = (state: OrderStore) => {
  return !!(
    state.uploadedImage &&
    state.selectedSize &&
    state.selectedFrame &&
    state.selectedMat
  );
};
